import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import { ErrorHandler, importProvidersFrom, isDevMode, LOCALE_ID, provideZonelessChangeDetection } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { LayoutComponent } from '@commons/layout/layout.component';
import { matSnackBarConfig } from '@config/angular-material.config';
import { bugsnagConfig } from '@config/bugsnag.config';
import { CcoService } from '@core/services/cco/cco.service';
import { CcoServiceImpl } from '@core/services/cco/cco.service.impl';
import { ContactDataService } from '@core/services/contact-data/contact-data.service';
import { ContactDataServiceImpl } from '@core/services/contact-data/contact-data.service.impl';
import { JwtService } from '@core/services/jwt.service';
import { REDIRECTION_TRACKING_SERVICE_CONFIG } from '@core/services/redirection/redirection-tracking-service.config';
import { RedirectionTrackingService } from '@core/services/redirection/redirection-tracking.service';
import { THEMED_FAQ_SERVICE_CONFIG } from '@core/services/themed-faq.service';
import { wizbiiWebservicesConfig } from '@core/wizbii-webservices.providers';
import { BankingIbanValidator } from '@domains/validators/banking-iban-validator';
import { BANKING_IBAN_VALIDATOR_CONFIG } from '@domains/validators/banking-iban-validator-config';
import { environment } from '@environment';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as abtestEffects from '@stores/abtest/abtest-ngrx.effect';
import { abtestKey, abTestReducer } from '@stores/abtest/abtest-ngrx.reducer';
import * as bankingEffects from '@stores/banking/banking-ngrx.effect';
import { bankingKey, bankingReducer } from '@stores/banking/banking-ngrx.reducer';
import * as dossiersEffects from '@stores/dossier/dossier-ngrx.effect';
import { dossierReducer, dossiersKey } from '@stores/dossier/dossier-ngrx.reducer';
import * as featureFlagsEffects from '@stores/features-flags/features-flags-ngrx.effect';
import { featureFlagsKey, featureFlagsReducer } from '@stores/features-flags/features-flags-ngrx.reducer';
import * as serviceEffects from '@stores/services/services.effect';
import { serviceKey, serviceReducer } from '@stores/services/services.reducer';
import * as sessionEffects from '@stores/session/session-ngrx.effect';
import { sessionKey, sessionReducer } from '@stores/session/session-ngrx.reducer';
import { JWT_SERVICE_TOKEN, JwtInterceptor } from '@wizbii-utils/angular/jwt';
import { IMAGINARY_PIPE_CONFIG } from '@wizbii-utils/angular/pipes';
import {
  jwtDeleteEffect,
  jwtDeleteHardEffect,
  jwtFeatureKey,
  jwtReducer,
  jwtSetEffect,
  loadJwt,
  NGRX_JWT_STATE_CONFIG,
} from '@wizbii-utils/angular/stores';
import { QuicklinkModule, QuicklinkStrategy } from 'ngx-quicklink';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeDe, 'de');

const client = Bugsnag.start(bugsnagConfig);

bootstrapApplication(LayoutComponent, {
  providers: [
    provideStore(),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: false, // If set to true, the connection is established within the Angular zone
      name: 'money-ngrx',
    }),
    provideState({
      name: jwtFeatureKey,
      reducer: jwtReducer,
    }),
    provideState({
      name: abtestKey,
      reducer: abTestReducer,
    }),
    provideState({
      name: sessionKey,
      reducer: sessionReducer,
    }),
    provideState({
      name: featureFlagsKey,
      reducer: featureFlagsReducer,
    }),
    provideState({
      name: serviceKey,
      reducer: serviceReducer,
    }),
    provideState({
      name: dossiersKey,
      reducer: dossierReducer,
    }),
    provideState({
      name: bankingKey,
      reducer: bankingReducer,
    }),
    provideState({
      name: 'router',
      reducer: routerReducer,
    }),
    provideZonelessChangeDetection(),
    provideRouterStore(),
    provideEffects(
      {
        loadJwt,
        jwtDeleteEffect,
        jwtSetEffect,
        jwtDeleteHardEffect,
      },
      serviceEffects,
      abtestEffects,
      sessionEffects,
      featureFlagsEffects,
      dossiersEffects,
      bankingEffects
    ),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: JWT_SERVICE_TOKEN, useClass: JwtService },
    {
      provide: NGRX_JWT_STATE_CONFIG,
      useValue: {
        jwtCookieName: 'wizbii_tokens',
        expiryCookieName: 'wizbii_expiry',
        appEnvFqdn: environment.envFqdn,
      },
    },
    { provide: ErrorHandler, useFactory: () => new BugsnagErrorHandler(client) },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: matSnackBarConfig },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    {
      provide: IMAGINARY_PIPE_CONFIG,
      useValue: {
        imaginaryUrl: environment.api.imaginary,
      },
    },
    ...wizbiiWebservicesConfig,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
    { provide: LOCALE_ID, useValue: environment.i18n.locale.replace('_', '-') },
    { provide: ContactDataService, useClass: ContactDataServiceImpl },
    { provide: CcoService, useClass: CcoServiceImpl },
    {
      provide: THEMED_FAQ_SERVICE_CONFIG,
      useValue: {
        baseUrl: environment.api.strapi,
        lang: environment.i18n.lang,
      },
    },
    {
      provide: BANKING_IBAN_VALIDATOR_CONFIG,
      useClass: BankingIbanValidator,
    },
    {
      provide: REDIRECTION_TRACKING_SERVICE_CONFIG,
      useClass: RedirectionTrackingService,
    },
    provideAnimations(),
    provideRouter(
      [
        {
          path: '',
          loadChildren: () => import('./app/features/routes').then((module) => module.FEATURES_ROUTES),
        },
      ],
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withPreloading(QuicklinkStrategy),
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      })
    ),
    importProvidersFrom(QuicklinkModule),
    importProvidersFrom(NgxSkeletonLoaderModule),
  ],
}).catch((err: unknown) => {
  // eslint-disable-next-line no-console
  console.error(err);
});
